import React, { useEffect, useState } from 'react';
import BucketsLoading from './BucketsLoading';
import AppModal from '../../../components/AppModal/AppModal';
import { api } from '../../../app/http-common';

// Corrections spec §4.3 — manual stale-suggestion archive.
//
// Always previews first: the confirmation must state the rule, the cutoff (or
// the latest-fill timestamp), the scope and the total number affected BEFORE
// anything is modified. Matched Suggestions are archived (recoverable), never
// deleted, and the caller is handed the ids so it can offer Undo.
// §1.9 — exact wording Or specified.
const RULE_LABELS = {
  in_earlier_list: 'Included in earlier lists',
  before_fill: 'Added before the latest automated fill',
  before_today: 'Added before today',
};

// §A5 — the membership rule has no cutoff to show, so the confirmation has to
// say what "earlier lists" means in its place. Stating the guarantee matters
// more than the count: the fear this action raises is losing something the
// client has not seen yet, and that cannot happen.
const RULE_EXPLANATIONS = {
  in_earlier_list:
    'Only suggestions that already appeared in a list sent, copied or linked to the client. '
    + 'Anything never issued to them is kept.',
};

export default function StaleArchiveModal({ request, onDone, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { rule, bucketId, clientId, scopeLabel } = request || {};

  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    (async () => {
      try {
        const res = await api.previewStaleArchive({ rule, bucketId, clientId }, { silent: true });
        if (!cancelled) setPreview(res);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not calculate the affected suggestions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [request, rule, bucketId, clientId]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const res = await api.applyStaleArchive({ rule, bucketId, clientId }, { silent: true });
      onDone(res);
    } catch (err) {
      setError(err?.response?.data?.error || 'The archive could not be applied.');
      setSubmitting(false);
    }
  };

  if (!request) return null;

  const total = preview?.total ?? 0;
  const cutoff = preview?.buckets?.[0]?.cutoff;
  const perBucket = preview?.buckets?.filter((b) => b.count > 0) || [];
  const skipped = preview?.skipped || [];

  return (
    <AppModal
      isOpen
      title="Archive old suggestions"
      onCancel={onClose}
      onConfirm={total > 0 && !loading ? confirm : undefined}
      confirmLabel={`Archive ${total}`}
      confirmDisabled={total === 0 || submitting}
      isSubmitting={submitting}
      maxWidth={520}
    >
      <div className="pbnew-scope">
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          <strong>{RULE_LABELS[rule]}</strong>
        </p>
        {RULE_EXPLANATIONS[rule] && (
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#3C4A5E', lineHeight: 1.5 }}>
            {RULE_EXPLANATIONS[rule]}
          </p>
        )}
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#3C4A5E' }}>
          {/* §1.9 — stories are archived, not deleted, and can be recovered
              through the archive filter. Undo is offered right after the action
              lands too. Or's spec asked for "how the grace period is applied";
              per product decision there is no grace period, only the cutoff
              time above. */}
          Stories will be <strong>archived</strong> (recoverable) rather than deleted.
        </p>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#3C4A5E' }}>
          Scope: <strong>{scopeLabel}</strong>
        </p>

        {loading && <BucketsLoading size={130} />}
        {error && <p style={{ fontSize: 13, color: '#c62828' }}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Single-bucket scope has one cutoff; publisher-wide computes one
                per bucket, so the per-bucket breakdown is shown instead. */}
            {cutoff && perBucket.length === 1 && (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#3C4A5E' }}>
                Cutoff: <strong>{new Date(cutoff).toLocaleString()}</strong>
                {preview.buckets[0].timeZone ? ` (${preview.buckets[0].timeZone})` : ''}
              </p>
            )}
            <p style={{ margin: '12px 0 0', fontSize: 14, color: '#0B1E3F' }}>
              <strong>{total}</strong> active suggestion{total === 1 ? '' : 's'} will be archived.
            </p>

            {perBucket.length > 1 && (
              <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, color: '#3C4A5E' }}>
                {perBucket.map((b) => (
                  <li key={b.bucketId}>
                    {b.bucketName}: <strong>{b.count}</strong>
                    {b.cutoff ? ` — before ${new Date(b.cutoff).toLocaleString()}` : ''}
                  </li>
                ))}
              </ul>
            )}

            {skipped.length > 0 && (
              <div className="pbnew-warn-box">
                Skipped {skipped.length} bucket{skipped.length === 1 ? '' : 's'}:
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {skipped.map((b) => <li key={b.bucketId}>{b.bucketName} — {b.reason}</li>)}
                </ul>
              </div>
            )}

            <p style={{ margin: '14px 0 0', fontSize: 12, color: '#3C4A5E' }}>
              Archived suggestions are recoverable — they are not deleted. Accepted, In work,
              Completed, Sent to publisher, Published, Rejected and already-archived stories are
              left unchanged.
            </p>
          </>
        )}
      </div>
    </AppModal>
  );
}
